import {
  assignPrototypeAPIs,
  assignTableAPIs,
  type CellData,
  type ColumnDef,
  type Row,
  type RowData,
  type Table_Internal,
  type TableFeature,
  type TableFeatures,
  type TableOptions,
} from '@tanstack/react-table';
import { uniqueId } from 'lodash-es';
import {
  Children,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  useMemo,
} from 'react';
import { isElement } from 'react-is';

import type {
  DataTableExpandableRowProps,
  DataTableRowDetailState,
  RowDetailsFeatureConstructors,
} from './row-details-types.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { DATA_TABLE_ROW_DETAILS_COLUMN_ID } from '../../constants.js';
import type { DataTableRowData } from '../../public.api.js';
import type { TDataTableFeatures } from '../types/features.types.js';

/**
 * DataTable slot selection for expandable rows.
 * @public
 */
export function DataTableExpandableRowTemplate<TData extends DataTableRowData>(
  props: DataTableExpandableRowProps<TData>,
) {
  return null;
}

/**
 * Type guard to ensure that a react element is a `DataTable.ExpandableRow` element.
 * @internal
 */
function isDataTableExpandableRowTemplate<TData extends DataTableRowData>(
  child: unknown,
): child is ReactElement<DataTableExpandableRowProps<TData>> {
  return isElement(child) && child.type === DataTableExpandableRowTemplate;
}

/**
 * Helper function to identify the ROW_DETAILS_COLUMN
 * @internal
 */
export function isRowDetailsColumnDef<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: ColumnDef<TFeatures, TData, TValue>): boolean {
  return column.id === DATA_TABLE_ROW_DETAILS_COLUMN_ID;
}

/**
 * Configuration hook for expandable rows of the DataTable.
 * @internal
 */
export function useRowDetails<TData extends DataTableRowData>(
  props: PropsWithChildren,
  options: TableOptions<TDataTableFeatures, Exclude<TData, unknown>>,
): void {
  const { children } = props;
  const rowDetailsTemplateProps = useMemo(() => {
    for (const child of Children.toArray(children)) {
      if (isDataTableExpandableRowTemplate<TData>(child)) {
        return child?.props;
      }
    }
  }, [children]);

  const [openRowDetails, setOpenRowDetails] = useControllableState({
    value: rowDetailsTemplateProps?.expandedRows,
    defaultValue: rowDetailsTemplateProps?.defaultExpandedRows,
    onChange: (value) => {
      if (value !== true) {
        rowDetailsTemplateProps?.onExpandedRowsChange?.(value);
      }
    },
  });

  const template = rowDetailsTemplateProps?.children;
  if (template !== undefined && typeof template === 'function') {
    // eslint-disable-next-line react-hooks/immutability
    options.enableRowDetails = true;
    // Cast is necessary to align the public template interface with the internal rowData
    // eslint-disable-next-line react-hooks/immutability
    options.rowDetailsTemplate = template as (params: {
      row: RowData;
    }) => ReactNode;
    // Cast is necessary to align the public template interface with the internal rowData
    // eslint-disable-next-line react-hooks/immutability
    options.rowDetailsDisableRow = rowDetailsTemplateProps?.disableExpand as (
      row: DataTableRowData,
    ) => boolean;

    // eslint-disable-next-line react-hooks/immutability
    options.state = {
      ...options.state,
      rowDetails: openRowDetails,
    };
    // eslint-disable-next-line react-hooks/immutability
    options.onRowDetailsOpenChange = setOpenRowDetails;
  }
}

/**
 * Allows access to the row model coming before the row details.
 */
export function table_getPreRowDetailsRowModel<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>) {
  return table.getPaginatedRowModel();
}

/**
 * Allows access to the row details model if available.
 */
export function table_getRowDetailsRowModel<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>) {
  table._rowModels.detailsRowModel ??=
    table.options._rowModels?.detailsRowModel?.(table);

  if (!table.options.enableRowDetails || !table._rowModels.detailsRowModel) {
    return table.getPreRowDetailsRowModel();
  }

  return table._rowModels.detailsRowModel();
}

/**
 * Helper to get a unique id for the table instance.
 * @internal
 */
export function table_rowDetailsTableId<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>) {
  if (!table._rowDetailsTableId) {
    table._rowDetailsTableId = uniqueId('DataTable-');
  }
  return table._rowDetailsTableId;
}

/**
 * Determines if the details row for a particular row is open.
 * @internal
 */
export function row_getIsDetailsOpen<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>): boolean {
  const detailOpenState = row.table.atoms.rowDetails?.get() ?? false;

  if (typeof detailOpenState === 'boolean') {
    return detailOpenState;
  }
  return detailOpenState[row.id] === true;
}

/**
 * Defines the grid position for the row details row.
 * @internal
 */
export function row_getRowDetailsGridPosition<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>) {
  return {
    gridColumnStart: 1,
    gridColumnEnd: `span ${row.table.getVisibleLeafColumns().length}`,
  };
}

/**
 * Returns the aria control id to connect the expanding button to the
 * row details wrapper.
 * @internal
 */
export function row_getRowDetailsAriaControlsId<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>): string {
  return `${row.table.rowDetailsTableId()}-${row.id}-expand`;
}

/**
 * Determines if the details for this row can be shown.
 * @internal
 */
export function row_getCanOpenDetails<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>): boolean {
  if (typeof row.table.options.rowDetailsDisableRow === 'function') {
    return !row.table.options.rowDetailsDisableRow(row.original);
  }
  return true;
}

/**
 * Control command to toggle the state of the rowDetails.
 * @internal
 */
export function row_toggleDetails<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>) {
  // remember current scroll position
  const scrollOffset = row.table.getRowScrollOffset();

  // expand/collapse details
  row.table.options.onRowDetailsOpenChange?.(
    (prev: DataTableRowDetailState) => {
      let newRowDetailsState: DataTableRowDetailState = {};
      if (typeof prev === 'boolean' && prev === true) {
        const flatRows = row.table.getRowModel().flatRows;
        for (const flatRow of flatRows) {
          if (row.getCanOpenDetails() && row.id !== flatRow.id) {
            newRowDetailsState[flatRow.id] = true;
          }
        }
        return newRowDetailsState;
      }

      newRowDetailsState = { ...prev };
      if (newRowDetailsState[row.id] === true) {
        delete newRowDetailsState[row.id];
      } else {
        newRowDetailsState[row.id] = true;
      }
      return newRowDetailsState;
    },
  );

  // scroll back to original scroll position to avoid row jumping
  requestAnimationFrame(() => {
    row.table.scrollToRowOffset(scrollOffset);
  });
}

/**
 * Constructs the column row details for the table,
 * including API implementations and prototype assignments.
 * @internal
 */
export function constructRowDetailsFeature<
  TData extends RowData,
>(): TableFeature<RowDetailsFeatureConstructors<TData>> {
  return {
    getInitialState: (initialState) => {
      return {
        rowDetails: {},
        ...initialState,
      };
    },
    assignRowPrototype: (prototype, table) => {
      assignPrototypeAPIs('DataTableRowDetailsFeature', prototype, table, {
        row_getIsDetailsOpen: { fn: (row) => row_getIsDetailsOpen(row) },
        row_getRowDetailsGridPosition: {
          fn: (row) => row_getRowDetailsGridPosition(row),
        },
        row_getRowDetailsAriaControlsId: {
          fn: (row) => row_getRowDetailsAriaControlsId(row),
        },
        row_getCanOpenDetails: { fn: (row) => row_getCanOpenDetails(row) },
        row_toggleDetails: { fn: (row) => row_toggleDetails(row) },
      });
    },
    constructTableAPIs: (table) => {
      assignTableAPIs('DataTableRowDetailsFeature', table, {
        table_rowDetailsTableId: {
          fn: () => table_rowDetailsTableId(table),
        },
        table_getPreRowDetailsRowModel: {
          fn: () => table_getPreRowDetailsRowModel(table),
        },
        table_getRowDetailsRowModel: {
          fn: () => table_getRowDetailsRowModel(table),
        },
        table_getRowModel: {
          fn: () => table_getRowDetailsRowModel(table),
        },
      });
    },
  };
}

/**
 * Feature implementation for Row Details.
 * @internal
 */
export const DataTableRowDetailsFeature = constructRowDetailsFeature();
